import { Box } from '@material-ui/core';
import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useTranslation } from 'react-i18next';
import Dialog from 'components/Dialog';
import { AppContext } from 'context';
import { useClosureById } from 'hooks';
import { ClosureForm, DialogWidth, InventoriedOutboundStockBatch, InventoryFormInterface } from 'types';
import Loading from 'components/Loading';
import { ClosureContext } from 'pages/Closures/ClosureClose';
import ConfirmationPopInContent from 'components/Generic/ConfirmationPopInContent';
import { postInventoriedOutboundStocks } from 'services/apiOperations';
import { formatInventoriedOutboundStockBeforePost } from 'utils/normalizer';
import { FormStepComponentProps } from '../MultiStepForm';
import InventoryForm from '../InventoryForm';
import { postPreviewClosure } from './previewClosure';
import { ErrorMessage } from './Step3';

const Step2: React.FC<FormStepComponentProps<ClosureForm, ClosureContext>> = ({
  onSubmit,
  formName,
  setFormContext,
  previousStepData,
}) => {
  const { t } = useTranslation();
  const [isOpenPopinConfirmationOneInventoriedStocksZero, setIsOpenPopinConfirmationOneInventoriedStocksZero] =
    useState(false);
  const [isOpenPopinConfirmationInventoriedStocks, setIsOpenPopinConfirmationInventoriedStocks] = useState(false);
  const [inventoryForm, setInventoryForm] = useState<InventoryFormInterface | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();

  const closureId = previousStepData.closureId ?? '';
  const [closure] = useClosureById(closureId);

  const { handleSubmit } = useForm<ClosureForm>({
    mode: 'onBlur',
  });

  if (!closure) return <Loading />;

  const isAnnualClosure = 12 === closure.month || closure.data?.annualClosure;

  const onSubmitInventoryForm = async (inventory: InventoryFormInterface) => {
    setInventoryForm(inventory);

    if (
      inventory.conditionedStocks.some(conditionedStock => {
        return conditionedStock.inventoryConditioningQuantity === 0 && conditionedStock.inventoryWeight === 0;
      })
    ) {
      setIsOpenPopinConfirmationOneInventoriedStocksZero(true);
    } else {
      setIsOpenPopinConfirmationInventoriedStocks(true);
    }
  };

  const generatePreview = async () => {
    if (!closure || !sortingCenter) {
      return;
    }
    if (!isAnnualClosure) {
      const validationViolations = await postPreviewClosure(closure, sortingCenter, t);
      if (!validationViolations) {
        return;
      }
      setFormContext(validationViolations);
    }

    handleSubmit(onSubmit)();
  };

  const handleValidatePopinConfirmationInventoriedStocks = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    setIsOpenPopinConfirmationOneInventoriedStocksZero(false);
    setIsOpenPopinConfirmationInventoriedStocks(false);
    if (!sortingCenter) return;

    const formattedInventoriedOutboundStocks: InventoriedOutboundStockBatch = formatInventoriedOutboundStockBeforePost(
      inventoryForm,
      sortingCenter['@id'],
    );

    try {
      await postInventoriedOutboundStocks(formattedInventoriedOutboundStocks);
      await generatePreview();
    } catch (error: any) {
      const { status, response } = error;
      const errorMessage: string | undefined = response?.body?.['hydra:description'];
      if (status === 400 && errorMessage === ErrorMessage.MonthlyWastageCalculationError) {
        setFormContext({
          violations: [
            {
              code: null,
              message: ErrorMessage.MonthlyWastageCalculationError,
              propertyPath: ErrorMessage.MonthlyWastageCalculationError,
              title: '',
            },
          ],
        });
        handleSubmit(onSubmit)();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box px={8} pt={6}>
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={isOpenPopinConfirmationOneInventoriedStocksZero}
        onClose={() => setIsOpenPopinConfirmationOneInventoriedStocksZero(false)}
        width={DialogWidth.SMALL_FORM}
        renderContent={() => (
          <ConfirmationPopInContent
            title={t('closures.closureConfirmationPopInOneInventoriedStocksZero.title')}
            cancelText={t('no')}
            validationText={t('yes')}
            cancelAction={() => setIsOpenPopinConfirmationOneInventoriedStocksZero(false)}
            validateAction={handleValidatePopinConfirmationInventoriedStocks}
          />
        )}
      />
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={isOpenPopinConfirmationInventoriedStocks}
        onClose={() => setIsOpenPopinConfirmationInventoriedStocks(false)}
        width={DialogWidth.SMALL_FORM}
        renderContent={() => (
          <ConfirmationPopInContent
            title={t('closures.closureConfirmationPopInInventoriedStocks.title')}
            cancelText={t('no')}
            validationText={t('yes')}
            cancelAction={() => setIsOpenPopinConfirmationInventoriedStocks(false)}
            validateAction={handleValidatePopinConfirmationInventoriedStocks}
          />
        )}
      />
      <InventoryForm
        formName={formName}
        onSubmit={onSubmitInventoryForm}
        year={closure.year.toString()}
        month={closure.month.toString()}
        isClosedMonth={false}
        inventoryEditable
        isDialogReset={true}
      />
    </Box>
  );
};

export default Step2;
